import React from 'react';
import { useNavigate } from 'react-router-dom';

import styled from 'styled-components';

import {
  COMING_SOON_FEATURE,
  COMING_SOON_FEATURES,
  COMING_SOON_ROUTE,
  ComingSoonFeatureKey,
} from 'components/ComingSoonFeatures/constants';
import { getOrdinalSuffix } from 'components/View/utils';
import { canAccessComingSoonFeatures, hasAISpeedToLeadFlag } from 'helpers/permissions';
import { useAppSelector } from 'store/hook';

import AICISummaryCard from './AICISummaryCard';
import { AICI_ANALYSIS_SCOPE, AICI_SWITCHER_VIEW, AICIAnalysisScope, AICISwitcherView } from './constants';
import useAICIAnalysisAvailability from './useAICIAnalysisAvailability';

const Container = styled.div`
  display: flex;
  gap: 20px;
  margin: 0 0 20px;

  @media (max-width: 650px) {
    flex-direction: column;
  }
`;

const RankingSubtitle = styled.span`
  color: var(--text-secondary);
  font-family: var(--font-regular);
  font-size: 12px;
  span {
    color: var(--text-primary);
  }
`;

const PilotSubtitle = styled.span`
  color: var(--positive-green);
`;

type Props = {
  activeView: AICISwitcherView;
  scope?: AICIAnalysisScope;
};

const AICIAnalysisSwitcher = ({ activeView, scope = AICI_ANALYSIS_SCOPE.COMMUNITY }: Props) => {
  const navigate = useNavigate();
  const { currentUser } = useAppSelector((state) => state.auth);
  const { selectedCommunity } = useAppSelector((state) => state.global);
  const communityId = selectedCommunity?.id;
  const isCompanyScope = scope === AICI_ANALYSIS_SCOPE.COMPANY;
  const {
    analyzedSpeedToLeadCommunities,
    canAccessOverall,
    competitorsCount,
    hasSpeedToLeadAnalysis,
    isLoading,
    overallRanking,
    speedToLeadRanking,
  } = useAICIAnalysisAvailability({ communityId, scope });
  const showSpeedToLeadTab = hasSpeedToLeadAnalysis;
  const isSpeedToLeadReleased = hasAISpeedToLeadFlag(currentUser) || showSpeedToLeadTab;
  const releasedComingSoonFeatures: ComingSoonFeatureKey[] = isSpeedToLeadReleased
    ? [COMING_SOON_FEATURE.SPEED_TO_LEAD]
    : [];
  const comingSoonFeatures = canAccessComingSoonFeatures(currentUser)
    ? COMING_SOON_FEATURES.filter((feature) => !releasedComingSoonFeatures.includes(feature.key))
    : [];

  const openOverall = () => {
    if (isCompanyScope) {
      navigate('/ai-competitive-intelligence/overall-market-position');
      return;
    }
    if (communityId) {
      navigate(`/ai-competitive-intelligence/overall-market-position?communityId=${communityId}`);
    }
  };

  const openSpeedToLead = () => {
    if (isCompanyScope) {
      navigate('/ai-competitive-intelligence/speed-to-lead');
      return;
    }
    if (communityId) {
      navigate(`/ai-competitive-intelligence/speed-to-lead?communityId=${communityId}`);
    }
  };

  const openComingSoon = (feature: ComingSoonFeatureKey) =>
    navigate(`${COMING_SOON_ROUTE}/${feature}${isCompanyScope ? '' : `?communityId=${communityId}`}`);

  if (isLoading || (!canAccessOverall && !showSpeedToLeadTab && !comingSoonFeatures.length)) {
    return null;
  }

  // Rankings include the selected community; competitorsCount includes only its competitors.
  const getRankingSubtitle = (ranking: number | null, fallback: string) =>
    ranking != null && competitorsCount > 0 ? (
      <RankingSubtitle>
        <span>{getOrdinalSuffix(ranking)}</span> among {competitorsCount + 1} competing
      </RankingSubtitle>
    ) : (
      fallback
    );

  return (
    <Container>
      {canAccessOverall && (
        <AICISummaryCard
          title="Overall Market Position"
          iconClassName="standalone"
          iconImageSrc="/icons/overall-market-position.svg"
          subtitle={
            isCompanyScope ? 'Portfolio intelligence' : getRankingSubtitle(overallRanking, 'Competitive intelligence')
          }
          selected={activeView === AICI_SWITCHER_VIEW.OVERALL}
          onClick={openOverall}
        />
      )}
      {showSpeedToLeadTab && (
        <AICISummaryCard
          title="Speed to Lead"
          iconClassName="standalone"
          iconImageSrc="/icons/speed-to-lead.svg"
          subtitle={
            isCompanyScope
              ? `${analyzedSpeedToLeadCommunities} communities analyzed`
              : getRankingSubtitle(speedToLeadRanking, 'Latest analysis')
          }
          selected={activeView === AICI_SWITCHER_VIEW.SPEED_TO_LEAD}
          onClick={openSpeedToLead}
        />
      )}
      {comingSoonFeatures.map((feature) => (
        <AICISummaryCard
          key={feature.key}
          title={feature.label}
          iconClassName={feature.iconClassName}
          iconImageSrc={feature.iconImageSrc}
          subtitle={feature.isPilot ? <PilotSubtitle>Ready to pilot</PilotSubtitle> : 'Coming soon'}
          selected={activeView === feature.key}
          onClick={() => openComingSoon(feature.key)}
        />
      ))}
    </Container>
  );
};

export default AICIAnalysisSwitcher;
